import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DESKTOP_INITIAL, MOBILE_INITIAL, getInitialVisible, matchesStateSearch } from "./Detailconstants.js";
import { fetchContent } from "../../../../services/contentService";
import ContactModal from "./ContactModal.jsx";

/* ================================================================
   BADGE TYPE RESOLVER
   ================================================================ */
function getBadgeClass(type) {
  const map = {
    FRRO: "VF-badge--frro",
    FRO: "VF-badge--fro",
    "Passport Office": "VF-badge--passport",
    "Visa Application": "VF-badge--vac",
    "Visa Application Centre": "VF-badge--vac",
  };
  return map[type] || "VF-badge--frro";
}

/* ================================================================
   OFFICE CARD
   ================================================================ */
function OfficeCard({ office, index }) {
  return (
    <article
      className="VF-office-card"
      data-aos="fade-up"
      data-aos-delay={Math.min(index * 60, 300)}
      aria-label={office.officeName ?? "Office"}
    >
      <div className="VF-office-card__header">
        
         
        <div className="VF-office-card__title-area">
          <h3 className="VF-office-card__name">{office.officeName ?? ""}</h3>
          <p className="VF-office-card__location">{office.city ?? ""}, {office.state ?? ""}</p>
        </div>
        <span className={`VF-badge ${getBadgeClass(office.type)}`}>{office.type}</span>
      </div>

      <div className="VF-office-card__body">
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
          </svg>
          <span className="VF-info-row__text">{office.address ?? ""}</span>
        </div>
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.76a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.02z"/>
          </svg>
          <span className="VF-info-row__text">
            <a href={`tel:${office.phone ?? ""}`}>{office.phone ?? ""}</a>
          </span>
        </div>
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
          <span className="VF-info-row__text">
            <a href={`mailto:${office.email ?? ""}`}>{office.email ?? ""}</a>
          </span>
        </div>
        <div className="VF-info-row">
          <svg className="VF-info-row__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span className="VF-info-row__text">{office.workingHours ?? ""}</span>
        </div>
        {office.services?.length > 0 && (
          <div className="VF-office-card__services" aria-label="Services offered">
            {office.services.map((svc) => (
              <span key={svc} className="VF-service-tag">{svc}</span>
            ))}
          </div>
        )}
      </div>

      <div className="VF-office-card__footer">
        <a href={office.website ?? "#"} target="_blank" rel="noopener noreferrer" className="VF-card-link VF-card-link--primary">
          Official Website
        </a>
        <a href={`mailto:${office.email ?? ""}`} className="VF-card-link VF-card-link--secondary">
          Send Email
        </a>
      </div>
    </article>
  );
}

/* ================================================================
   VISA OFFICES SECTION
   ================================================================ */
export default function VisaOfficesSection() {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [visibleCount, setVisibleCount] = useState(getInitialVisible);
  const [modalOpen, setModalOpen] = useState(false);

  const stateOptions = useMemo(() => {
    const unique = [...new Set(offices.map((o) => o.state).filter(Boolean))].sort();
    return unique;
  }, [offices]);

  const typeOptions = useMemo(() => {
    const unique = [...new Set(offices.map((o) => o.type).filter(Boolean))].sort();
    return unique;
  }, [offices]);

  useEffect(() => {
    fetchContent("visaDetail").then(section => {
      const list = section?.content?.visaOffices;
      if (Array.isArray(list) && list.length) {
        const mapped = list.map((o, idx) => ({
          ...o,
          id: o.id || `office-${idx}`,
          officeName: o.office_name || o.officeName,
          type: o.office_type || o.type,
          workingHours: o.working_hours || o.workingHours,
        }));
        setOffices(mapped);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return offices.filter((office) => {
      const matchSearch =
        !q ||
        (office.officeName?.toLowerCase().includes(q) ?? false) ||
        (office.city?.toLowerCase().includes(q) ?? false) ||
        matchesStateSearch(office.state, q) ||
        (office.address?.toLowerCase().includes(q) ?? false) ||
        (office.services?.some((s) => s?.toLowerCase().includes(q)) ?? false);
      const matchState = !selectedState || matchesStateSearch(office.state, selectedState);
      const matchType = !selectedType || office.type === selectedType;
      return matchSearch && matchState && matchType;
    });
  }, [search, selectedState, selectedType, offices]);

  const isOutOfScope = search.trim().length > 0 && filtered.length === 0;

  const handleReset = useCallback(() => {
    setSearch("");
    setSelectedState("");
    setSelectedType("");
    setVisibleCount(getInitialVisible());
  }, []);

  const hasFilters = search || selectedState || selectedType;
  const visibleCards = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const canCollapse = visibleCount > getInitialVisible() && !hasMore;

  const handleViewMore = () => setVisibleCount((p) => p + (window.innerWidth <= 767 ? MOBILE_INITIAL : DESKTOP_INITIAL));
  const handleCollapse = () => setVisibleCount(getInitialVisible());

  return (
    <section className="VF-section VF-section--alt" id="visa-offices" aria-label="The Offices Section">
      <div className="VF-container">
        <header className="VF-section-header" data-aos="fade-up">
          <h2 className="VF-heading-lg">Visa Offices&amp; FRROs</h2>
          <span className="VF-divider" aria-hidden="true" />
          <p>Locate Foreigners Regional Registration Offices, FROs and Passport Offices across all major Indian states.</p>
        </header>

        <div className="VF-filter-bar" data-aos="fade-up" data-aos-delay="80" role="search" aria-label="Filter visa offices">
          <div className="VF-filter-group VF-filter-group--search">
            {/* <label className="VF-filter-label" htmlFor="VF-offices-search">Search</label> */}
            <input
              id="VF-offices-search"
              type="text"
              className="VF-filter-input"
              placeholder="Search by name, city, state, or service..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(getInitialVisible()); }}
              aria-label="Search visa offices"
            />
          </div>
          <div className="VF-filter-row">
            <div className="VF-filter-group">
              {/* <label className="VF-filter-label" htmlFor="VF-offices-state">State</label> */}
              <select id="VF-offices-state" className="VF-filter-select" value={selectedState} onChange={(e) => { setSelectedState(e.target.value); setVisibleCount(getInitialVisible()); }}>
                <option value="">State</option>
                {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="VF-filter-group">
              <select id="VF-offices-type" className="VF-filter-select" value={selectedType} onChange={(e) => { setSelectedType(e.target.value); setVisibleCount(getInitialVisible()); }}>
                <option value="">Type</option>
                {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && (
            <button className="VF-filter-reset" onClick={handleReset}>Reset Filters</button>
          )}
        </div>

        <div className="VF-results-info" aria-live="polite" aria-atomic="true">
          <p className="VF-results-count">
            Showing <strong>{Math.min(visibleCount, filtered.length)}</strong> of <strong>{filtered.length}</strong> offices
          </p>
        </div>

        {loading ? (
          <div className="VF-empty-state" role="status"><p className="VF-empty-state__text">Loading visa offices...</p></div>
        ) : filtered.length > 0 ? (
          <>
            <div className="VF-cards-grid" role="list">
              {visibleCards.map((office, i) => (
                <div key={office.id} role="listitem">
                  <OfficeCard office={office} index={i} />
                </div>
              ))}
            </div>
            {(hasMore || canCollapse) && (
              <div className="VF-view-more-wrapper">
                {hasMore && (
                  <button className="VF-btn-view-more" onClick={handleViewMore}>
                    Show More Offices
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    {/* <span className="VF-show-more-count">({filtered.length - visibleCount} more)</span> */}
                  </button>
                )}
                {canCollapse && (
                  <button className="VF-btn-view-more VF-btn-view-more--collapse" onClick={handleCollapse}>
                    Show Less
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="VF-empty-state" data-aos="fade-up" role="status">
            <h3 className="VF-empty-state__title">No offices found</h3>
            <p className="VF-empty-state__text">
              {isOutOfScope
                ? "We couldn't find anything matching your search. Submit an enquiry and we'll help you directly."
                : "Try adjusting your search terms or resetting the filters."}
            </p>
            <div className="VF-empty-state__actions">
              <button className="VF-btn-secondary" onClick={handleReset}>Reset Filters</button>
              {isOutOfScope && (
                <button className="VF-btn-primary" onClick={() => setModalOpen(true)}>Submit Enquiry</button>
              )}
            </div>
          </div>
        )}
      </div>

      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} query={search} sectionName="The Offices" />
    </section>
  );
}